import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import {
    Table,
    Input,
    Button,
    Space,
    Popconfirm,
    Typography,
    message,
    Card,
    Row,
    Col,
    Dropdown,
    Modal
} from 'antd';
import {
    SearchOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    QuestionCircleOutlined
} from '@ant-design/icons';

const { Title } = Typography;

const PriceTypesPage = () => {
    const [priceTypes, setPriceTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState('ascend');

    // Состояния для добавления и редактирования
    const [isAdding, setIsAdding] = useState(false);
    const [newPriceTypeName, setNewPriceTypeName] = useState('');
    const [editingPriceTypeId, setEditingPriceTypeId] = useState(null);
    const [editName, setEditName] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        fetchPriceTypes();
    }, [pagination.current, pagination.pageSize, sortField, sortOrder]);

    const fetchPriceTypes = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            const params = {
                page: pagination.current,
                limit: pagination.pageSize,
                search: searchText,
                sortField,
                sortOrder: sortOrder === 'ascend' ? 'asc' : 'desc'
            };

            const response = await axios.get('/api/price-types', {
                params,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            setPriceTypes(response.data.data || []);
            setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
        } catch (error) {
            message.error('Ошибка при загрузке типов цен');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddPriceType = async () => {
        if (!newPriceTypeName.trim()) {
            message.warning('Название обязательно');
            return;
        }

        const token = localStorage.getItem('token');
        try {
            await axios.post('/api/price-types', { name: newPriceTypeName }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            setIsAdding(false);
            setNewPriceTypeName('');
            message.success('Тип цены добавлен');
            fetchPriceTypes();
        } catch (error) {
            message.error('Не удалось добавить тип цены');
        }
    };

    const handleSearch = () => {
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchPriceTypes();
    };

    const handleDoubleClick = (priceType) => {
        setEditingPriceTypeId(priceType.id);
        setEditName(priceType.name);
    };

    const saveEditedPriceType = async (priceTypeId) => {
        if (!editName.trim()) {
            message.warning('Название обязательно');
            return;
        }

        const token = localStorage.getItem('token');
        try {
            await axios.put(`/api/price-types/${priceTypeId}`, { name: editName.trim() }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            setEditingPriceTypeId(null);
            message.success('Сохранено');
            fetchPriceTypes();
        } catch (error) {
            message.error('Ошибка при сохранении');
        }
    };

    const handleDelete = async (id) => {
        const token = localStorage.getItem('token');
        try {
            await axios.delete(`/api/price-types/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            message.success('Удалено');
            fetchPriceTypes();
        } catch (error) {
            message.error('Ошибка при удалении');
        }
    };

    const handleTableChange = (pag, filters, sorter) => {
        setPagination(prev => ({ ...prev, current: pag.current, pageSize: pag.pageSize }));
        if (sorter.field) {
            setSortField(sorter.field);
            setSortOrder(sorter.order);
        }
    };

    const getContextMenu = (record) => [
        {
            key: 'edit',
            label: 'Редактировать',
            icon: <EditOutlined />,
            onClick: () => handleDoubleClick(record)
        },
        {
            key: 'delete',
            label: 'Удалить',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => handleDelete(record.id)
        }
    ];

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 100,
        },
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            sortOrder: sortField === 'name' ? sortOrder : null,
            render: (text, record) => {
                if (editingPriceTypeId === record.id) {
                    return (
                        <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onBlur={() => saveEditedPriceType(record.id)}
                            onPressEnter={() => saveEditedPriceType(record.id)}
                            onKeyDown={(e) => e.key === 'Escape' && setEditingPriceTypeId(null)}
                            autoFocus
                        />
                    );
                }
                return (
                    <div
                        onDoubleClick={() => handleDoubleClick(record)}
                        style={{ cursor: 'pointer', minHeight: '24px', width: '100%' }}
                    >
                        {text}
                    </div>
                );
            }
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 120,
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Button 
                        type="text" 
                        icon={<EditOutlined style={{ color: '#1890ff' }} />} 
                        onClick={() => handleDoubleClick(record)} 
                    />
                    <Popconfirm
                        title="Удалить тип цены?"
                        icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                        onConfirm={() => handleDelete(record.id)}
                        okText="Да"
                        cancelText="Нет"
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Layout>
            <div style={{ padding: '24px' }}>
                <Card bordered={false}>
                    <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 24 }}>
                        <Col flex="auto">
                            <Title level={3} style={{ margin: 0 }}>Управление типами цен</Title>
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setIsAdding(true)}
                            >
                                Добавить тип цены
                            </Button>
                        </Col>
                    </Row>

                    <div style={{ marginBottom: 16 }}>
                        <Input.Search
                            placeholder="Поиск по названию"
                            allowClear
                            enterButton="Поиск"
                            size="large"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onSearch={handleSearch}
                            style={{ maxWidth: 400 }}
                        />
                    </div>

                    {isAdding && (
                        <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }} title="Новый тип цены">
                            <Space style={{ width: '100%' }}>
                                <Input
                                    placeholder="Введите название"
                                    value={newPriceTypeName}
                                    onChange={(e) => setNewPriceTypeName(e.target.value)}
                                    style={{ width: 400 }}
                                    autoFocus
                                    onPressEnter={handleAddPriceType}
                                />
                                <Button type="primary" onClick={handleAddPriceType}>Сохранить</Button>
                                <Button onClick={() => { setIsAdding(false); setNewPriceTypeName(''); }}>Отмена</Button>
                            </Space>
                        </Card>
                    )}

                    <Table
                        columns={columns}
                        dataSource={priceTypes}
                        rowKey="id"
                        loading={loading}
                        onChange={handleTableChange}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            pageSizeOptions: ['5', '10', '20', '50'],
                            showTotal: (total) => `Всего: ${total}`
                        }}
                        components={{
                            body: {
                                row: (props) => {
                                    const record = priceTypes.find(p => p.id === props['data-row-key']);
                                    if (!record) return <tr {...props} />;
                                    return (
                                        <Dropdown 
                                            menu={{ items: getContextMenu(record) }} 
                                            trigger={['contextMenu']}
                                        >
                                            <tr {...props} />
                                        </Dropdown>
                                    );
                                }
                            }
                        }}
                    />
                </Card>
            </div>
        </Layout>
    );
};

export default PriceTypesPage;